import { Package } from "lucide-react";
import type { TProductWithRelation } from "@/types/product-type";
import { usePosContext } from "../context/pos-context";

type PosProductCardProps = {
  productWithRelation: TProductWithRelation;
};

export function PosProductCard({ productWithRelation }: PosProductCardProps) {
  const { cart, handleOpenProductDetail } = usePosContext();
  const { product, productVariants } = productWithRelation;
  const variant = productVariants?.[0];
  const variantId = variant?.id ?? product.id;
  const price = variant?.price ?? product.price;
  const imageUrl = variant?.imageUrl ?? product.imageUrl;
  const name = variant?.name ?? product.name;
  const displayPrice = typeof price === "number" ? price.toFixed(2) : "0.00";
  const hasMultipleVariants = (productVariants?.length ?? 0) > 1;
  const quantityInCart = hasMultipleVariants
    ? (productVariants ?? []).reduce(
        (sum, v) => sum + cart.getQuantity(v.id),
        0
      )
    : cart.getQuantity(variantId);

  const handleAddToCart = () => {
    cart.addItem({
      productId: product.id,
      variantId,
      name,
      imageUrl: imageUrl ?? null,
      price: typeof price === "number" ? price : 0,
    });
  };

  const handleCardClick = () => {
    if (hasMultipleVariants) {
      handleOpenProductDetail(productWithRelation);
    } else {
      handleAddToCart();
    }
  };

  return (
    <div className="w-full rounded-lg md:rounded-xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-shadow duration-200">
      <button
        type="button"
        onClick={handleCardClick}
        className="relative aspect-[4/3] bg-gray-50 w-full group"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <Package className="h-10 w-10 md:h-12 md:w-12 text-gray-400" />
          </div>
        )}
        {quantityInCart > 0 && (
          <span
            className="absolute top-1.5 right-1.5 md:top-2 md:right-2 inline-flex items-center justify-center min-w-[20px] md:min-w-[24px] h-5 md:h-6 px-1.5 md:px-2 rounded-full bg-primary text-primary-foreground text-xs md:text-sm font-semibold shadow-md"
            aria-label={`${quantityInCart} in cart`}
          >
            {quantityInCart}
          </span>
        )}
      </button>

      <div className="p-2 md:p-3 space-y-1.5 md:space-y-2">
        <button
          type="button"
          onClick={handleCardClick}
          className="w-full text-left"
        >
          <div className="flex items-center justify-between gap-1.5 md:gap-2">
          <h3 className="font-semibold text-foreground text-xs md:text-sm leading-snug line-clamp-1">
            {product.name}
          </h3>
          <p className="text-base md:text-lg font-bold text-primary">
            ${displayPrice}
          </p>
          </div>
        </button>
      </div>
    </div>
  );
}
